import { useEffect, useState } from "react";
import { Button, Upload, message } from "antd";
import { gql, useLazyQuery } from "@apollo/client";

const GENERATE_SIGNED_URL = gql`
  query GenerateSignedUrl($payload: UploadUserImageInput!) {
    generateSingedURL(payload: $payload) {
      signedURL
    }
  }
`;

// Supported file types
const SUPPORTED_FILE_TYPES = {
  // Image formats
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",

  // Document formats
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",

  // Text formats
  "text/plain": "txt",
  "text/csv": "csv",
  "application/json": "json",
  "application/xml": "xml",

  // Archive formats
  "application/zip": "zip",
  "application/x-rar-compressed": "rar",
  "application/x-7z-compressed": "7z",
};

export default function PutUpload({
  fileName = "image.png",
  onChange,
  accept = "*/*",
  maxSize = 10 * 1024 * 1024, // Default 10MB
  showUploadList = true,
  listType = "picture",
  defaultFileType = "png", // Default file format, use png if not provided
  autoGenerateUrl = true, // Whether to auto-generate URL, set to false for uncertain file formats
}) {
  const [uploadAction, setUploadAction] = useState("");
  const [generateSignedUrl, { urlData }] = useLazyQuery(GENERATE_SIGNED_URL);
  const [currentFile, setCurrentFile] = useState(null);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);

  // Initialize upload URL (only generate when autoGenerateUrl is true)
  useEffect(() => {
    if (autoGenerateUrl) {
      generateSignedUrl({
        variables: {
          payload: {
            contentType: defaultFileType,
            fileName,
          },
        },
      }).then((v) => {
        setUploadAction(v.data.generateSingedURL.signedURL);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlData, generateSignedUrl, defaultFileType, autoGenerateUrl]);

  // Get file extension
  const getFileExtension = (file) => {
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf(".");
    return lastDotIndex !== -1
      ? fileName.substring(lastDotIndex + 1).toLowerCase()
      : "";
  };

  // Get file type
  const getFileType = (file) => {
    const extension = getFileExtension(file);
    const mimeType = file.type;

    // Prioritize MIME type
    if (SUPPORTED_FILE_TYPES[mimeType]) {
      return SUPPORTED_FILE_TYPES[mimeType];
    }

    // If no MIME type, try to infer from extension
    const extensionMap = {
      png: "png",
      jpg: "jpg",
      jpeg: "jpg",
      gif: "gif",
      webp: "webp",
      svg: "svg",
      pdf: "pdf",
      doc: "doc",
      docx: "docx",
      xls: "xls",
      xlsx: "xlsx",
      ppt: "ppt",
      pptx: "pptx",
      txt: "txt",
      csv: "csv",
      json: "json",
      xml: "xml",
      zip: "zip",
      rar: "rar",
      "7z": "7z",
    };

    return extensionMap[extension] || "file";
  };

  // Generate upload URL
  const generateUploadUrl = async (file) => {
    const fileType = getFileType(file);
    const timestamp = Date.now();
    const fileNameWithExt = `${fileName}_${timestamp}.${fileType}`;

    setIsGeneratingUrl(true);
    try {
      const result = await generateSignedUrl({
        variables: {
          payload: {
            contentType: fileType,
            fileName: fileNameWithExt,
          },
        },
      });
      const signedUrl = result.data.generateSingedURL.signedURL;
      setUploadAction(signedUrl);
      setIsGeneratingUrl(false);
      return signedUrl;
    } catch (error) {
      console.error("Failed to generate upload URL:", error);
      message.error("Failed to generate upload URL");
      setIsGeneratingUrl(false);
      throw error;
    }
  };
  // File validation before upload
  const beforeUpload = async (file) => {
    // Check file size
    if (file.size > maxSize) {
      message.error(
        `File size cannot exceed ${(maxSize / 1024 / 1024).toFixed(1)}MB`
      );
      // Clear previous URL when validation fails
      setUploadAction("");
      setCurrentFile(null);
      return false;
    }

    // Check file type
    const fileType = getFileType(file);
    if (fileType === "file" && file.type === "") {
      message.warning(
        "Unable to identify file type, please ensure the file format is correct"
      );
    }

    // Save current file
    setCurrentFile(file);

    // Always generate a new URL for the selected file
    // This ensures a fresh URL is generated even after validation failures
    try {
      await generateUploadUrl(file);
      // Ensure URL generation completes before continuing
      return new Promise((resolve) => {
        // Use setTimeout to ensure state update completes
        setTimeout(() => {
          resolve(true);
        }, 100);
      });
    } catch (error) {
      message.error("Failed to generate upload URL");
      // Clear URL state on error
      setUploadAction("");
      setCurrentFile(null);
      return false;
    }
  };

  const handleUploadChange = (info) => {
    if (info.file.status === "done") {
      const fileUrl = uploadAction.split("?Content-Type=")[0];
      onChange && onChange(fileUrl, currentFile || info.file);
    } else if (info.file.status === "error") {
      message.error("File upload failed, please try again");
    }
  };

  const onUpload = ({ action, file, onError, onProgress, onSuccess }) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", action, true);

    // Set Content-Type
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    );

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.floor((event.loaded / event.total) * 100);
        onProgress({ percent });
      }
    };

    xhr.onerror = (event) => {
      onError(event);
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          onSuccess(xhr.response, xhr);
        } else {
          onError(new Error(xhr.statusText), xhr);
        }
      }
    };

    xhr.send(file);
  };

  return (
    <Upload
      name="logo"
      action={uploadAction}
      listType={listType}
      showUploadList={showUploadList}
      accept={accept}
      beforeUpload={beforeUpload}
      onChange={handleUploadChange}
      customRequest={onUpload}
      maxCount={1}
      disabled={isGeneratingUrl}
    >
      <Button loading={isGeneratingUrl} disabled={isGeneratingUrl}>
        {isGeneratingUrl ? "Generating URL..." : "Upload"}
      </Button>
    </Upload>
  );
}
